import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getHero, updateHero, getAbout, updateAbout, getGlobals, updateGlobals, getServices, updateService, uploadSiteImage, getSettings, getSectionHeaders, updateSectionHeader, updateUiStrings, getUiStrings, getBlogPosts, updateBlogTexts } from "../../lib/api";
import { DEFAULT_LOCALE, EMPTY_NO_CHANGES, TOAST_UPDATED, TOAST_LOAD_ERROR, TOAST_NO_PERMISSION, TOAST_IMAGE_UPLOADED, TOAST_IMAGE_ERROR, TOAST_SAVE_ERROR, TOAST_ADD_ERROR, PLH_CTA_SECONDARY, PLH_CTA_TERTIARY, PLH_WA_MESSAGE, PLH_SCROLL, PLH_SERVICES_EYEBROW, PLH_SERVICES_HEADLINE, PLH_SERVICES_LEARN_MORE, PLH_SERVICES_DESC, PLH_SECTION_EYEBROW, PLH_SECTION_HEADLINE, PLH_SECTION_DESC, PLH_ALL_CATEGORIES, PLH_NO_PROJECTS, PLH_FAQ_EYEBROW, PLH_FAQ_HEADLINE, PLH_FAQ_DESC, PLH_BLOG_EYEBROW, PLH_BLOG_HEADLINE, PLH_BLOG_DESC, PLH_IMG_ALT } from "../../lib/constants";
import { Save, Layout, Info, Briefcase, ChevronRight, Loader2, ImageIcon, EyeOff, Share2, MousePointer2, Languages, Type, List, FormInput, ChevronDown, HelpCircle, Star, BookOpen } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { logger } from "../../lib/logger";
import AdminHeroEditor from "./AdminHeroEditor";
import AdminAboutEditor from "./AdminAboutEditor";
import AdminServicesEditor from "./AdminServicesEditor";
import AdminIdentityEditor from "./AdminIdentityEditor";
import AdminSectionsEditor from "./AdminSectionsEditor";
import AdminSharingEditor from "./AdminSharingEditor";

import AdminUiConfig from "./AdminUiConfig";
import AdminLocalesEditor from "./AdminLocalesEditor";
import AdminFaq from "./AdminFaq";
import AdminTestimonials from "./AdminTestimonials";
import AdminPortfolio from "./AdminPortfolio";
import AdminBlogEditor from "./AdminBlogEditor";
import { getLocales } from "../../lib/api";
import { SITE_CONTENT_TABS as TABS } from "../../lib/tabs";

const _tabLoaded = {};
let _localesLoaded = false;

export default function AdminSiteContent({ readOnly = false, subTabModes = {}, defaultSubTab, onSubTabChange, defaultSub2, onSub2Change }) {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ hero: false, about: false, services: {} });
  const [availableLangs, setAvailableLangs] = useState([{ code: DEFAULT_LOCALE, label: "PT", name: "Português" }]);
  const [activeLang, setActiveLang] = useState(DEFAULT_LOCALE);
  const [loaded, setLoaded] = useState({});

  const [hero, setHero] = useState(null);
  const [about, setAbout] = useState(null);
  const [services, setServices] = useState([]);
  const [globals, setGlobals] = useState(null);
  const [globalsDirty, setGlobalsDirty] = useState({});
  function updateGlobalsField(next) {
    setGlobals(next);
    setGlobalsDirty(prev => {
      const nextDirty = { ...prev };
      Object.keys(next).forEach(k => {
        if (globals?.[k] !== next[k]) nextDirty[k] = true;
      });
      return nextDirty;
    });
  }
  const [sections, setSections] = useState(null);
  const [sectionHeaders, setSectionHeaders] = useState(null);
  const [ogSettings, setOgSettings] = useState({ og_title: "", og_description: "", og_image: "", og_url: "" });
  const [tabLoading, setTabLoading] = useState({});
  const [activeTab, setActiveTab] = useState(defaultSubTab || "hero");
  const [formSubTab, setFormSubTab] = useState("labels");
  const [faqSubTab, setFaqSubTab] = useState(defaultSub2 === "faqs" || defaultSub2 === "questions" || defaultSub2 === "categories" || defaultSub2 === "texts" ? defaultSub2 : "faq_faqs");
  const [heroSubTab, setHeroSubTab] = useState(defaultSub2 === "content" || defaultSub2 === "texts" ? defaultSub2 : "content");
  const [settingsSubTab, setSettingsSubTab] = useState(defaultSub2 === "globals" || defaultSub2 === "sections" || defaultSub2 === "sharing" || defaultSub2 === "locales" ? defaultSub2 : "globals");
  const [serviceSubTab, setServiceSubTab] = useState(defaultSub2 === "service" || defaultSub2 === "texts" ? defaultSub2 : "service");
  const [portfolioSubTab, setPortfolioSubTab] = useState(defaultSub2 === "items" || defaultSub2 === "texts" ? defaultSub2 : "items");
  const [testimonialSubTab, setTestimonialSubTab] = useState(defaultSub2 === "testimonial_items" || defaultSub2 === "texts" ? defaultSub2 : "testimonial_items");
  const [blogSubTab, setBlogSubTab] = useState(defaultSub2 === "items" || defaultSub2 === "texts" ? defaultSub2 : "items");
  const [menuSubTab, setMenuSubTab] = useState(defaultSub2 === "lists" || defaultSub2 === "texts" ? defaultSub2 : "lists");
  const [svcTexts, setSvcTexts] = useState({ eyebrow: "", headline: "", description: "", learn_more: "" });
  const [portfolioTexts, setPortfolioTexts] = useState({ eyebrow: "", headline: "", description: "", all_categories: "", no_projects: "" });
  const [testimonialTexts, setTestimonialTexts] = useState({ eyebrow: "", headline: "", description: "" });
  const [blogTexts, setBlogTexts] = useState({ eyebrow: "", headline: "", description: "", read_more: "" });
  const [heroTexts, setHeroTexts] = useState({ cta_primary: "", cta_secondary: "", whatsapp_message: "", scroll: "" });
  const [heroTextsDirty, setHeroTextsDirty] = useState({});
  const [svcDirty, setSvcDirty] = useState({});
  const [portfolioDirty, setPortfolioDirty] = useState({});
  const [testimonialDirty, setTestimonialDirty] = useState({});
  const [blogDirty, setBlogDirty] = useState({});

  function makeDirtyHandler(setValue, setDirty) {
    return (field) => (e) => {
      setValue(prev => ({ ...prev, [field]: e.target.value }));
      setDirty(prev => ({ ...prev, [field]: true }));
    };
  }
  const svcOnChange = makeDirtyHandler(setSvcTexts, setSvcDirty);
  const portfolioOnChange = makeDirtyHandler(setPortfolioTexts, setPortfolioDirty);
  const testimonialOnChange = makeDirtyHandler(setTestimonialTexts, setTestimonialDirty);
  const blogOnChange = makeDirtyHandler(setBlogTexts, setBlogDirty);
  const heroTextsOnChange = makeDirtyHandler(setHeroTexts, setHeroTextsDirty);

  async function saveDirty(context, values, dirty, setDirty, lang) {
    const ui = await getUiStrings(lang);
    const isNew = !ui?.[context] || Object.keys(ui[context]).length === 0;
    if (!isNew && Object.keys(dirty).filter(k => dirty[k]).length === 0) { toast.info(EMPTY_NO_CHANGES); return; }
    const payload = isNew ? { ...values } : {};
    if (!isNew) Object.keys(dirty).filter(k => dirty[k]).forEach(k => { payload[k] = values[k]; });
    await updateUiStrings(context, payload, lang);
    setDirty({});
    toast.success(TOAST_UPDATED);
  }

  useEffect(() => {
    if (defaultSub2) {
      if (activeTab === "hero" && defaultSub2 !== heroSubTab) setHeroSubTab(defaultSub2);
      if (activeTab === "settings" && defaultSub2 !== settingsSubTab) setSettingsSubTab(defaultSub2);
      if (activeTab === "services" && defaultSub2 !== serviceSubTab) setServiceSubTab(defaultSub2);
      if (activeTab === "portfolio" && defaultSub2 !== portfolioSubTab) setPortfolioSubTab(defaultSub2);
      if (activeTab === "testimonials" && defaultSub2 !== testimonialSubTab) setTestimonialSubTab(defaultSub2);
      if (activeTab === "blog" && defaultSub2 !== blogSubTab) setBlogSubTab(defaultSub2);
      if (activeTab === "faq" && defaultSub2 !== faqSubTab) setFaqSubTab(defaultSub2);
      if (activeTab === "form" && defaultSub2 !== formSubTab) setFormSubTab(defaultSub2);
      if (activeTab === "menu" && defaultSub2 !== menuSubTab) setMenuSubTab(defaultSub2);
    }
  }, [activeTab, defaultSub2]);

  const LOADERS = {
    hero: () => getHero(activeLang).then(d => setHero(d)),
    about: () => getAbout(activeLang).then(d => setAbout(d)),
    services: () => {
      const ps = [];
      console.log("[AdminSiteContent] services loader, serviceSubTab:", serviceSubTab);
      if (serviceSubTab === "service") { ps.push(getServices(activeLang).then(d => setServices(d))); }
      return Promise.all(ps);
    },
    blog: () => getBlogPosts(activeLang).then(d => {}),
    identity: () => getGlobals().then(d => setGlobals(d)),
    sections: () => getSettings().then(d => { setSections({ hero: d?.section_hero_visible !== "false", about: d?.section_about_visible !== "false", services: d?.section_services_visible !== "false", portfolio: d?.section_portfolio_visible !== "false", testimonials: d?.section_testimonials_visible !== "false", faq: d?.section_faq_visible !== "false", contact: d?.section_contact_visible !== "false", hero_cta: d?.element_hero_cta_visible !== "false", hero_scroll: d?.element_hero_scroll_visible !== "false", about_image: d?.element_about_image_visible !== "false", about_stats: d?.element_about_stats_visible !== "false", services_details: d?.element_services_details_visible !== "false", services_carousel: d?.element_services_carousel_visible !== "false", faq_form: d?.element_faq_form_visible !== "false", faq_items: d?.element_faq_items_visible !== "false", contact_info: d?.element_contact_info_visible !== "false", contact_form: d?.element_contact_form_visible !== "false" }); setOgSettings({ og_title: d?.og_title || "", og_description: d?.og_description || "", og_image: d?.og_image || "", og_url: d?.og_url || "" }); }),
    sharing: () => getSettings().then(d => setOgSettings({ og_title: d?.og_title || "", og_description: d?.og_description || "", og_image: d?.og_image || "", og_url: d?.og_url || "" })),
    settings: () => Promise.all([
      getGlobals().then(d => setGlobals(d)),
      getSettings().then(d => {
        setSections({ hero: d?.section_hero_visible !== "false", about: d?.section_about_visible !== "false", services: d?.section_services_visible !== "false", portfolio: d?.section_portfolio_visible !== "false", testimonials: d?.section_testimonials_visible !== "false", faq: d?.section_faq_visible !== "false", contact: d?.section_contact_visible !== "false", hero_cta: d?.element_hero_cta_visible !== "false", hero_scroll: d?.element_hero_scroll_visible !== "false", about_image: d?.element_about_image_visible !== "false", about_stats: d?.element_about_stats_visible !== "false", services_details: d?.element_services_details_visible !== "false", services_carousel: d?.element_services_carousel_visible !== "false", faq_form: d?.element_faq_form_visible !== "false", faq_items: d?.element_faq_items_visible !== "false", contact_info: d?.element_contact_info_visible !== "false", contact_form: d?.element_contact_form_visible !== "false" });
        setOgSettings({ og_title: d?.og_title || "", og_description: d?.og_description || "", og_image: d?.og_image || "", og_url: d?.og_url || "" });
      }),
      getLocales().then(() => {}),
    ]),
  };

  function loadAvailableLangs() {
    getLocales().then(data => {
      if (!Array.isArray(data)) return;
      const active = data.filter(l => l.active === 1 && !l.deleted_at);
      if (active.length > 0) {
        const mapped = active.map(l => ({ code: l.code, label: l.label?.substring(0, 2).toUpperCase() || l.code, name: l.label, flag: l.flag || "" }));
        setAvailableLangs(mapped);
        if (!mapped.find(l => l.code === activeLang)) setActiveLang(mapped[0].code);
      }
    }).catch(() => {});
  }

  useEffect(() => {
    if (_localesLoaded) return;
    _localesLoaded = true;
    loadAvailableLangs();
  }, []);

  function handleLocalesChanged() {
    loadAvailableLangs();
  }

  useEffect(() => {
    if (activeTab === "settings") return;
    Object.keys(_tabLoaded).forEach(k => delete _tabLoaded[k]);
    setHero(null); setAbout(null); setServices([]); setGlobals(null); setSections(null); setSectionHeaders(null);
    setOgSettings({ og_title: "", og_description: "", og_image: "", og_url: "" });
    setSvcDirty({}); setPortfolioDirty({}); setTestimonialDirty({}); setHeroTextsDirty({});
  }, [activeLang, activeTab]);

  const needsUiStrings = ["hero", "services", "portfolio", "testimonials", "blog"].includes(activeTab);
  useEffect(() => {
    if (!needsUiStrings) return;
    getUiStrings(activeLang).then(d => {
      const g = d?.general || {};
      setHeroTexts({ cta_primary: g.cta_primary || "", cta_secondary: g.cta_secondary || "", whatsapp_message: g.whatsapp_message || "", scroll: g.scroll || "" });
      const s = d?.service || {};
      setSvcTexts({ eyebrow: s.eyebrow || "", headline: s.headline || "", description: s.description || "", learn_more: s.learn_more || "" });
      const p = d?.portfolio_section || {};
      setPortfolioTexts({ eyebrow: p.eyebrow || "", headline: p.headline || "", description: p.description || "", all_categories: g.all_categories || "", no_projects: g.no_projects || "" });
      const t = d?.testimonials_section || {};
      setTestimonialTexts({ eyebrow: t.eyebrow || "", headline: t.headline || "", description: t.description || "" });
      const b = d?.blog_section || {};
      setBlogTexts({ eyebrow: b.eyebrow || "", headline: b.headline || "", description: b.description || "", read_more: b.read_more || "" });
    }).catch(() => {});
  }, [activeLang, needsUiStrings]);

  useEffect(() => {
    if (_tabLoaded[activeTab]) { console.log("[AdminSiteContent] cache hit for", activeTab); return; }
    const loader = LOADERS[activeTab];
    if (!loader) { console.log("[AdminSiteContent] no loader for", activeTab); return; }
    console.log("[AdminSiteContent] loading", activeTab, "serviceSubTab:", serviceSubTab);
    _tabLoaded[activeTab] = true;
    setTabLoading(p => ({ ...p, [activeTab]: true }));
      loader().catch(err => { logger.error(err); toast.error(TOAST_LOAD_ERROR); })
      .finally(() => { setTabLoading(p => ({ ...p, [activeTab]: false })); });
  }, [activeTab, activeLang, heroSubTab, settingsSubTab, serviceSubTab, portfolioSubTab, testimonialSubTab, blogSubTab, faqSubTab, formSubTab, menuSubTab]);

  useEffect(() => { delete _tabLoaded[activeTab]; }, [serviceSubTab, portfolioSubTab, testimonialSubTab, blogSubTab, faqSubTab]);

  useEffect(() => {
    if (onSub2Change && activeTab === "hero") onSub2Change(heroSubTab);
    if (onSub2Change && activeTab === "settings") onSub2Change(settingsSubTab);
    if (onSub2Change && activeTab === "form") onSub2Change(formSubTab);
    if (onSub2Change && activeTab === "menu") onSub2Change(menuSubTab);
  }, [formSubTab]);

  async function handleImageUpload(e, target, serviceId = null) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    const file = e.target.files?.[0];
    if (!file) return;
    if (serviceId) {
      setUploading(prev => ({ ...prev, services: { ...prev.services, [serviceId]: true } }));
    } else {
      setUploading(prev => ({ ...prev, [target]: true }));
    }
    try {
      const url = await uploadSiteImage(file);
      if (serviceId) {
        setServices(services.map(s => s.uid === serviceId ? { ...s, image_url: url } : s));
        toast.success(TOAST_IMAGE_UPLOADED);
      } else if (target === "hero") {
        setHero({ ...hero, bg_image: url });
        toast.success(TOAST_IMAGE_UPLOADED);
      } else if (target === "about") {
        setAbout({ ...about, image: url });
        toast.success(TOAST_IMAGE_UPLOADED);
      }
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_IMAGE_ERROR);
    } finally {
      if (serviceId) {
        setUploading(prev => ({ ...prev, services: { ...prev.services, [serviceId]: false } }));
      } else {
        setUploading(prev => ({ ...prev, [target]: false }));
      }
    }
  }

  function handleRemoveImage(target, serviceId = null) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (serviceId) {
      setServices(services.map(s => s.uid === serviceId ? { ...s, image_url: "" } : s));
    } else if (target === "hero") {
      setHero({ ...hero, bg_image: "" });
    } else if (target === "about") {
      setAbout({ ...about, image: "" });
    }
  }

  async function handleSaveHero() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    setSaving(true);
    try { 
      await updateHero(hero, activeLang); 
      toast.success(`Atualizado (${activeLang}).`); 
    }
    catch (err) { logger.error(err); toast.error(TOAST_SAVE_ERROR); }
    finally { setSaving(false); }
  }

  async function handleSaveAbout() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    setSaving(true);
    try { 
      await updateAbout(about, activeLang); 
      toast.success(`Atualizado (${activeLang}).`); 
    }
    catch (err) { logger.error(err); toast.error(TOAST_SAVE_ERROR); }
    finally { setSaving(false); }
  }

  async function handleSaveGlobals() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    const dirty = Object.keys(globalsDirty).filter(k => globalsDirty[k]);
    if (dirty.length === 0) { toast.info(EMPTY_NO_CHANGES); return; }
    setSaving(true);
    try { 
      const payload = {};
      dirty.forEach(k => { payload[k] = globals[k]; });
      await updateGlobals(payload);
      setGlobalsDirty({});
      toast.success(TOAST_UPDATED);
    }
    catch (err) { logger.error(err); toast.error(TOAST_SAVE_ERROR); }
    finally { setSaving(false); }
  }

  async function handleSaveService(id, updates) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    try { 
      await updateService(id, updates, activeLang); 
      toast.success(`Atualizado (${activeLang}).`); 
    }
    catch (err) { logger.error(err); toast.error(TOAST_SAVE_ERROR); }
  }

  async function handleSaveSectionHeaders(sectionFilter) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    setSaving(true);
    try {
      if (sectionFilter && !sectionHeaders?.[sectionFilter]) {
        await getSectionHeaders(activeLang).then(d => setSectionHeaders(d));
      }
      const headers = sectionFilter ? (sectionHeaders?.[sectionFilter] ? { [sectionFilter]: sectionHeaders[sectionFilter] } : {}) : sectionHeaders;
      await updateUiStrings("section_headers", { data: JSON.stringify(headers) }, activeLang);
      toast.success(`Atualizado (${activeLang}).`);
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  }

  const activeLangName = availableLangs.find(l => l.code === activeLang)?.name || activeLang;

  return (
    <div data-id="admin-site-content" className="max-w-6xl mx-auto pb-20 flex flex-col gap-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-fendi/20 pb-8">
        <div>
          <h2 className="font-serif text-4xl text-softblack mb-2 flex items-center gap-3">Gestão do Site</h2>
          <p className="text-warmgray text-sm flex items-center gap-2">
            <MousePointer2 size={14} /> Personalize a identidade visual e o conteúdo da página inicial.
          </p>
        </div>
        <button onClick={() => window.open('/', '_blank')} className="btn-ghost !py-2 flex items-center gap-2">
          Ver Site Público <ChevronRight size={14} />
        </button>
      </div>

      {/* Language Selector */}
      <div className="bg-white border border-fendi/40 rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center text-champagne">
            <Languages size={20} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-softblack uppercase tracking-wider">Idioma de Conteúdo</h3>
            <p className="text-[11px] text-warmgray">Editando versão em: <span className="text-softblack font-medium">{activeLangName}</span></p>
          </div>
        </div>
        
        <div className="flex items-stretch gap-1.5 md:gap-2 p-1.5 md:p-1 bg-fendi/5 rounded-lg border border-fendi/20">
          {availableLangs.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setActiveLang(lang.code)}
              className={`flex-1 px-3 md:px-4 py-2 md:py-1.5 rounded-md text-[11px] font-bold tracking-widest transition-all text-center ${
                activeLang === lang.code
                  ? "bg-white text-softblack shadow-sm ring-1 ring-fendi/20"
                  : "text-warmgray hover:text-softblack hover:bg-white/50"
              }`}
            >
              {lang.flag || lang.label}
            </button>
          ))}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (onSubTabChange) onSubTabChange(v); }} className="w-full">
        {/* Mobile: 3-card layout */}
        <div className="md:hidden space-y-2.5 mb-3">
          {/* Card 1: Main menu tabs */}
          <div className="bg-white border border-fendi/30 rounded-xl py-3 px-2 shadow-sm">
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5">
              {TABS.map((t) => {
                const isActive = activeTab === t.value;
                return (
                   <button key={t.value} onClick={() => { setActiveTab(t.value); if (t.children) { const f = t.value === "form" ? setFormSubTab : t.value === "faq" ? setFaqSubTab : t.value === "services" ? setServiceSubTab : t.value === "portfolio" ? setPortfolioSubTab : t.value === "testimonials" ? setTestimonialSubTab : t.value === "blog" ? setBlogSubTab : () => {}; f(t.children[0].value); } }}
                    className={`flex flex-col items-center gap-0.5 px-1.5 py-1 rounded-lg transition-all ${isActive ? "bg-softblack/5" : "hover:bg-fendi/5"}`}>
                    <div className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${isActive ? "bg-softblack text-white shadow-sm" : "bg-transparent text-warmgray/60"}`}>
                      <t.icon size={16} />
                    </div>
                    <span className={`text-[6px] uppercase tracking-[0.12em] font-semibold ${isActive ? "text-softblack" : "text-warmgray/50"}`}>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Card 2: Sub-menu items (visible when active tab has children) */}
          {TABS.filter(t => t.children && activeTab === t.value).length > 0 && (() => {
            const activeTabData = TABS.find(t => t.value === activeTab);
            const subState = activeTab === "hero" ? heroSubTab : activeTab === "settings" ? settingsSubTab : activeTab === "form" ? formSubTab : activeTab === "faq" ? faqSubTab : activeTab === "services" ? serviceSubTab : activeTab === "portfolio" ? portfolioSubTab : activeTab === "testimonials" ? testimonialSubTab : activeTab === "blog" ? blogSubTab : activeTab === "menu" ? menuSubTab : null;
            return (
              <div className="bg-white border border-fendi/30 rounded-xl py-2.5 px-3 shadow-sm">
                <div className="flex flex-wrap justify-center gap-2">
                  {activeTabData.children.map((c) => {
                    const isSubActive = c.value === subState;
                    const SubIcon = c.icon || null;
                    return (
                      <button key={c.value} onClick={() => { setActiveTab(activeTab); const setSub = activeTab === "hero" ? setHeroSubTab : activeTab === "settings" ? setSettingsSubTab : activeTab === "form" ? setFormSubTab : activeTab === "faq" ? setFaqSubTab : activeTab === "services" ? setServiceSubTab : activeTab === "portfolio" ? setPortfolioSubTab : activeTab === "testimonials" ? setTestimonialSubTab : activeTab === "blog" ? setBlogSubTab : activeTab === "menu" ? setMenuSubTab : () => {}; setSub(c.value); if (onSub2Change) onSub2Change(c.value); }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[8px] uppercase tracking-widest rounded-lg transition-all ${isSubActive ? "bg-champagne/15 text-champagne font-bold shadow-sm border border-champagne/20" : "text-warmgray/60 hover:text-warmgray hover:bg-fendi/10 border border-transparent"}`}>
                        {SubIcon && <SubIcon size={11} />}
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="flex gap-0 bg-white border border-fendi rounded-xl overflow-hidden shadow-sm min-h-[500px]">
          <TabsList className="hidden md:flex flex-col w-52 shrink-0 border-r border-fendi p-0 h-auto justify-start rounded-none bg-fendi/5">
            {TABS.map((t) =>
              t.children ? (
                <div key={t.value} className="w-full">
                  <TabsTrigger value={t.value} onClick={() => setActiveTab(t.value)}
                    className="relative w-full justify-start gap-3 px-5 py-3.5 text-[11px] tracking-[0.15em] uppercase data-[state=active]:bg-white data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:right-0 after:top-2 after:bottom-2 after:w-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none text-warmgray hover:text-softblack hover:bg-white/50">
                    <t.icon size={15} />
                    <span className="flex-1 text-left">{t.label}</span>
                    <ChevronDown size={12} className={`transition-transform ${activeTab === t.value ? "rotate-180" : ""}`} />
                  </TabsTrigger>
                  {activeTab === t.value && (
                    <div className="border-t border-fendi/20 bg-fendi/5">
                      {t.children.map((c) => {
                        const getSubState = () => t.value === "hero" ? heroSubTab : t.value === "settings" ? settingsSubTab : t.value === "form" ? formSubTab : t.value === "faq" ? faqSubTab : t.value === "services" ? serviceSubTab : t.value === "portfolio" ? portfolioSubTab : t.value === "testimonials" ? testimonialSubTab : t.value === "blog" ? blogSubTab : t.value === "menu" ? menuSubTab : null;

                        const isActive = c.value === getSubState();

                        const setSub = t.value === "hero" ? setHeroSubTab : t.value === "settings" ? setSettingsSubTab : t.value === "form" ? setFormSubTab : t.value === "faq" ? setFaqSubTab : t.value === "services" ? setServiceSubTab : t.value === "portfolio" ? setPortfolioSubTab : t.value === "testimonials" ? setTestimonialSubTab : t.value === "blog" ? setBlogSubTab : t.value === "menu" ? setMenuSubTab : () => {};
                        const onSub2 = (v) => { setSub(v); if (onSub2Change) onSub2Change(v); };
                        return (
                          <button key={c.value}
                            onClick={() => { setActiveTab(t.value); onSub2(c.value); }}
                            className={`w-full text-left px-5 py-2.5 pl-12 text-[10px] tracking-wider transition-colors ${isActive ? "text-champagne font-bold bg-white" : "text-warmgray hover:text-softblack hover:bg-white/50"}`}>
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <TabsTrigger key={t.value} value={t.value} className="relative w-full justify-start gap-3 px-5 py-3.5 text-[11px] tracking-[0.15em] uppercase data-[state=active]:bg-white data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:right-0 after:top-2 after:bottom-2 after:w-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none text-warmgray hover:text-softblack hover:bg-white/50">
                  <t.icon size={15} /> {t.label}
                </TabsTrigger>
              )
            )}
          </TabsList>
          
          <div className="flex-1 p-4 md:p-10 relative min-w-0 overflow-hidden">
            {tabLoading[activeTab] && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-6 h-6 border-2 border-champagne border-t-transparent rounded-full animate-spin" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-warmgray animate-pulse">Carregando...</span>
                </div>
              </div>
            )}
            
            {activeTab === "hero" && heroSubTab === "content" && <AdminHeroEditor readOnly={readOnly} hero={hero} setHero={setHero} uploading={uploading} handleImageUpload={handleImageUpload} handleRemoveImage={handleRemoveImage} saving={saving} handleSaveHero={handleSaveHero} />}
            {activeTab === "hero" && heroSubTab === "texts" && (
              <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Type size={14} /></div>
                    <span className="text-xs font-medium text-softblack uppercase tracking-wider">Textos dos Botões do Hero</span>
                  </div>
                  {!readOnly && (
                    <button onClick={() => saveDirty("general", heroTexts, heroTextsDirty, setHeroTextsDirty, activeLang).catch(() => toast.error(TOAST_SAVE_ERROR))} className="btn-primary !py-1.5 !px-3 !text-[9px]">
                      <Save size={11} className="mr-1.5" /> Guardar
                    </button>
                  )}
                </div>
                <div className="p-6 grid sm:grid-cols-2 gap-6">
                  <div>
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Botão Principal (WhatsApp)</span>
                    <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={heroTexts.cta_primary} onChange={heroTextsOnChange("cta_primary")} placeholder={PLH_CTA_SECONDARY} />
                  </div>
                  <div>
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Botão Secundário (Serviços)</span>
                    <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={heroTexts.cta_secondary} onChange={heroTextsOnChange("cta_secondary")} placeholder={PLH_CTA_TERTIARY} />
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Mensagem Padrão do WhatsApp</span>
                    <textarea className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all resize-none" rows={2} value={heroTexts.whatsapp_message} onChange={heroTextsOnChange("whatsapp_message")} placeholder={PLH_WA_MESSAGE} />
                  </div>
                  <div>
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Texto: Scroll (Hero)</span>
                    <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={heroTexts.scroll} onChange={heroTextsOnChange("scroll")} placeholder={PLH_SCROLL} />
                  </div>
                </div>
              </div>
            )}
            {activeTab === "about" && <AdminAboutEditor readOnly={readOnly} about={about} setAbout={setAbout} uploading={uploading} handleImageUpload={handleImageUpload} handleRemoveImage={handleRemoveImage} saving={saving} handleSaveAbout={handleSaveAbout} />}
            {activeTab === "services" && serviceSubTab === "service" && <AdminServicesEditor readOnly={readOnly} services={services} setServices={setServices} uploading={uploading} setUploading={setUploading} handleSaveService={handleSaveService} activeLang={activeLang} />}
            {activeTab === "services" && serviceSubTab === "texts" && (
              <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0"><Type size={16} /></div>
                    <div>
                      <h3 className="text-sm font-medium text-softblack">Textos da Secção Serviços</h3>
                      <p className="text-[10px] text-warmgray mt-0.5">Cabeçalho, descrição e botão "Saber mais"</p>
                    </div>
                  </div>
                  {!readOnly && (
                    <button onClick={async () => {
                      const dirty = Object.keys(svcDirty).filter(k => svcDirty[k]);
                      if (dirty.length === 0) { toast.info(EMPTY_NO_CHANGES); return; }
                      try { await saveDirty("service", svcTexts, svcDirty, setSvcDirty, activeLang); }
                      catch (e) { logger.error(e); toast.error(e.message || TOAST_SAVE_ERROR); }
                    }} className="btn-primary !py-2 !px-4 !text-[10px] shrink-0">
                      <Save size={12} className="mr-1.5" /> Guardar
                    </button>
                  )}
                </div>
                <div className="p-6 space-y-6">
                  <div className="grid sm:grid-cols-3 gap-x-8 gap-y-5">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Eyebrow</span>
                      <input className="w-full mt-1.5 px-3 py-2 text-xs border border-fendi/20 rounded-md bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={svcTexts.eyebrow} onChange={svcOnChange("eyebrow")} placeholder={PLH_SERVICES_EYEBROW} />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Título</span>
                      <input className="w-full mt-1.5 px-3 py-2 text-xs border border-fendi/20 rounded-md bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={svcTexts.headline} onChange={svcOnChange("headline")} placeholder={PLH_SERVICES_HEADLINE} />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Botão</span>
                      <input className="w-full mt-1.5 px-3 py-2 text-xs border border-fendi/20 rounded-md bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={svcTexts.learn_more} onChange={svcOnChange("learn_more")} placeholder={PLH_SERVICES_LEARN_MORE} />
                    </label>
                    <div className="sm:col-span-2">
                      <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Descrição</span>
                      <textarea className="w-full mt-1.5 px-3 py-2 text-xs border border-fendi/20 rounded-md bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all resize-none leading-relaxed" rows={5} value={svcTexts.description} onChange={svcOnChange("description")} placeholder={PLH_SERVICES_DESC} />
                  </div>
                </div>
              </div>
            </div>
            )}
            {activeTab === "portfolio" && portfolioSubTab === "items" && <AdminPortfolio readOnly={readOnly} activeLang={activeLang} />}
            {activeTab === "portfolio" && portfolioSubTab === "texts" && <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
                <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Type size={14} /></div><span className="text-xs font-medium text-softblack uppercase tracking-wider">Textos do Portfólio</span></div>
                {!readOnly && <button onClick={async () => {
                  try {
                    const dirty = Object.keys(portfolioDirty).filter(k => portfolioDirty[k]);
                    if (dirty.length === 0) { toast.info(EMPTY_NO_CHANGES); return; }
                    const sectionDirty = dirty.filter(k => ["eyebrow","headline","description"].includes(k));
                    const generalDirty = dirty.filter(k => ["all_categories","no_projects"].includes(k));
                    await Promise.all([
                      sectionDirty.length > 0 ? updateUiStrings("portfolio_section", Object.fromEntries(sectionDirty.map(k => [k, portfolioTexts[k]])), activeLang) : Promise.resolve(),
                      generalDirty.length > 0 ? updateUiStrings("general", Object.fromEntries(generalDirty.map(k => [k, portfolioTexts[k]])), activeLang) : Promise.resolve(),
                    ]);
                    setPortfolioDirty({});
                    toast.success(TOAST_UPDATED);
                  } catch (e) { logger.error(e); toast.error(TOAST_SAVE_ERROR); }
                }} className="btn-primary !py-1.5 !px-3 !text-[9px]"><Save size={11} className="mr-1.5" /> Guardar</button>}
              </div>
              <div className="p-5 space-y-5">
                <div className="grid sm:grid-cols-3 gap-4">
                  {["eyebrow", "headline", "description"].map(f => (
                    <label key={f} className="block">
                      <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">{f === "eyebrow" ? "Eyebrow" : f === "headline" ? "Headline" : "Descrição"}</span>
                      <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={portfolioTexts[f]} onChange={portfolioOnChange(f)} placeholder={f === "eyebrow" ? PLH_SECTION_EYEBROW : f === "headline" ? PLH_SECTION_HEADLINE : PLH_SECTION_DESC} />
                    </label>
                  ))}
                </div>
                <div className="border-t border-fendi/10 pt-5 grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Filtro: Todas Categorias</span>
                    <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={portfolioTexts.all_categories} onChange={portfolioOnChange("all_categories")} placeholder={PLH_ALL_CATEGORIES} />
                  </label>
                  <label className="block">
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Aviso: Sem Projetos</span>
                    <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={portfolioTexts.no_projects} onChange={portfolioOnChange("no_projects")} placeholder={PLH_NO_PROJECTS} />
                  </label>
                </div>
              </div>
            </div>}
            {activeTab === "testimonials" && testimonialSubTab === "testimonial_items" && <AdminTestimonials readOnly={readOnly} activeLang={activeLang} />}
            {activeTab === "testimonials" && testimonialSubTab === "texts" && <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
                <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Type size={14} /></div><span className="text-xs font-medium text-softblack uppercase tracking-wider">Textos da Secção Depoimentos</span></div>
                {!readOnly && <button onClick={() => saveDirty("testimonials_section", testimonialTexts, testimonialDirty, setTestimonialDirty, activeLang).catch(() => toast.error(TOAST_SAVE_ERROR))} className="btn-primary !py-1.5 !px-3 !text-[9px]"><Save size={11} className="mr-1.5" /> Guardar</button>}
              </div>
              <div className="p-5 grid sm:grid-cols-3 gap-4">
                {["eyebrow", "headline", "description"].map(f => (
                  <label key={f} className="block">
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">{f === "eyebrow" ? "Eyebrow" : f === "headline" ? "Headline" : "Descrição"}</span>
                    <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={testimonialTexts[f]} onChange={testimonialOnChange(f)} placeholder={f === "eyebrow" ? PLH_SECTION_EYEBROW : f === "headline" ? PLH_SECTION_HEADLINE : PLH_SECTION_DESC} />
                  </label>
                ))}
              </div>
            </div>}
            {activeTab === "blog" && blogSubTab === "items" && <AdminBlogEditor readOnly={readOnly} activeLang={activeLang} />}
            {activeTab === "blog" && blogSubTab === "texts" && <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
                <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><BookOpen size={14} /></div><span className="text-xs font-medium text-softblack uppercase tracking-wider">Textos da Secção Blog</span></div>
                {!readOnly && <button onClick={() => saveDirty("blog_section", blogTexts, blogDirty, setBlogDirty, activeLang).catch(() => toast.error(TOAST_SAVE_ERROR))} className="btn-primary !py-1.5 !px-3 !text-[9px]"><Save size={11} className="mr-1.5" /> Guardar</button>}
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-4">
                {["eyebrow", "headline", "description", "read_more"].map(f => (
                  <label key={f} className="block">
                    <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">{f === "eyebrow" ? "Eyebrow" : f === "headline" ? "Headline" : f === "description" ? "Descrição" : "Ler mais"}</span>
                    <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={blogTexts[f]} onChange={blogOnChange(f)} placeholder={f === "eyebrow" ? PLH_BLOG_EYEBROW : f === "headline" ? PLH_BLOG_HEADLINE : f === "description" ? PLH_BLOG_DESC : "ex: Ler mais"} />
                  </label>
                ))}
              </div>
            </div>}
            {activeTab === "menu" && <AdminUiConfig readOnly={readOnly} subTabModes={subTabModes} initialTab={menuSubTab} embedded activeLang={activeLang} onActiveLangChange={setActiveLang} availableLangs={availableLangs} />}
            {activeTab === "footer" && <AdminUiConfig readOnly={readOnly} subTabModes={subTabModes} initialTab="interface" embedded activeLang={activeLang} onActiveLangChange={setActiveLang} availableLangs={availableLangs} />}
            {activeTab === "form" && <AdminUiConfig readOnly={readOnly} subTabModes={subTabModes} initialTab="form" formInitialTab={formSubTab} embedded activeLang={activeLang} onActiveLangChange={setActiveLang} availableLangs={availableLangs} sectionHeaders={sectionHeaders} setSectionHeaders={setSectionHeaders} handleSaveSectionHeaders={handleSaveSectionHeaders} saving={saving} />}
            {activeTab === "settings" && settingsSubTab === "globals" && <AdminIdentityEditor readOnly={readOnly} globals={globals} setGlobals={updateGlobalsField} saving={saving} handleSaveGlobals={handleSaveGlobals} />}
            {activeTab === "settings" && settingsSubTab === "sections" && <AdminSectionsEditor readOnly={readOnly} sections={sections} setSections={setSections} />}
            {activeTab === "settings" && settingsSubTab === "sharing" && <AdminSharingEditor ogSettings={ogSettings} setOgSettings={setOgSettings} saving={saving} activeLang={activeLang} />}
            {activeTab === "settings" && settingsSubTab === "locales" && <AdminLocalesEditor onChange={handleLocalesChanged} />}
            {activeTab === "faq" && (
              <AdminFaq key={faqSubTab} readOnly={readOnly} subTabModes={subTabModes} defaultTab={faqSubTab} onTabChange={(v) => { setFaqSubTab(v); if (onSub2Change) onSub2Change(v); }} activeLang={activeLang} />
            )}
          </div>
        </div>
      </Tabs>
    </div>
  );
}
